/*
 * CHI-20: ONE coherent registration-card state for EventDetail (John's LNY-2027
 * screenshot showed "Paid · unavailable · Complimentary" plus "You're Registered!"
 * with no ticket). Pure, so every combination is unit-tested.
 *
 * Root cause of "Complimentary": eventPricingPolicies.priceLabel defaults to
 * "Complimentary" in drizzle/schema.ts, and EventDetail fell back to it for
 * paid_internal events. A paid event never shows that label now.
 *
 * Applies to paid_internal events (ticketed via Stripe). Everything else returns
 * "legacy" and the existing block renders unchanged (free, external, G4.1 flows).
 */
import type { PublicEventPricingPolicy } from "./eventRegistrationFlow";
import type { MyTicket, TicketQuote } from "./ticketingApi";
import { ticketsCheckoutEnabled } from "./eventCta";

export type TicketCardState =
  | { kind: "legacy" }
  | { kind: "price"; unitAmountCents: number | null; fullAmountCents: number | null; currency: string; available: boolean; priceLabel: string | null }
  | { kind: "pending_approval" }
  | { kind: "finalizing" }
  | { kind: "registered"; ticketCount: number }
  /**
   * CHI-22 follow-up (Dev3): the viewer holds a blocking ticket (Dev2 hasBlockingTicket: active OR
   * suspended) but no active one, i.e. a suspended (disputed) order. Neutral and non-actionable:
   * no button, no My Ticket(s) link, no price CTA (a buy would 409).
   */
  | { kind: "under_review" };

/** Working default pending John (Dev3, 2026-10-31). Followed by the SUPPORT_EMAIL contact line. */
export const ORDER_UNDER_REVIEW_COPY = "Your order is under review";

export function isActiveIssuedTicket(t: Pick<MyTicket, "status">) {
  return t.status === "active";
}

export function resolveTicketCardState(input: {
  pricingPolicy?: (PublicEventPricingPolicy & { priceAmountCents?: number | null; currency?: string | null; priceLabel?: string | null }) | null;
  isSpecialEvent?: boolean;
  /** G4.1: special-event registration awaiting a decision. */
  awaitingApproval?: boolean;
  quote?: TicketQuote | null;
  /** From getMyTicketsForEvent (webhook-issued only). */
  tickets?: Pick<MyTicket, "status">[] | null;
  finalizing?: boolean;
  /** Dev2's hasBlockingTicket (myTicketsForEvent). With no active ticket it means a suspended order. */
  hasBlockingTicket?: boolean;
}): TicketCardState {
  const policy = input.pricingPolicy;
  if (policy?.pricingMode !== "paid_internal") return { kind: "legacy" };
  if (input.isSpecialEvent) return input.awaitingApproval ? { kind: "pending_approval" } : { kind: "legacy" };
  const active = (input.tickets ?? []).filter(isActiveIssuedTicket).length;
  if (active > 0) return { kind: "registered", ticketCount: active };
  if (input.hasBlockingTicket) return { kind: "under_review" };
  if (input.finalizing) return { kind: "finalizing" };
  const unit = input.quote?.unitPriceCents ?? policy.priceAmountCents ?? null;
  const full = input.quote?.promoActive ? input.quote.fullPriceCents : null;
  return {
    kind: "price",
    unitAmountCents: unit,
    fullAmountCents: full != null && unit != null && full > unit ? full : null,
    currency: input.quote?.currency ?? policy.currency ?? "usd",
    // CHI-20 kill switch: with TICKETS_CHECKOUT_ENABLED off the card offers no checkout (no Buy Tickets, no Get tickets).
    available: policy.checkoutAvailability === "available" && ticketsCheckoutEnabled(policy),
    // Server label only when it names a price; the "Complimentary" column default never shows on a paid event.
    priceLabel: policy.priceLabel && !/complimentary/i.test(policy.priceLabel) ? policy.priceLabel : null,
  };
}

/** Text for the card's "Registration" detail row. Never "Complimentary" for a paid event. */
export function ticketCardPriceText(state: TicketCardState, format: (cents: number, currency: string) => string) {
  switch (state.kind) {
    case "price": {
      const price = state.unitAmountCents != null ? `${format(state.unitAmountCents, state.currency)} per ticket` : state.priceLabel ?? "price shown at checkout";
      return state.available ? `Paid · ${price}` : `Paid · unavailable · ${price}`;
    }
    case "pending_approval":
      return "Paid · pending approval";
    case "finalizing":
      return "Paid · finalizing your tickets";
    case "under_review":
      return "Paid · order under review";
    case "registered":
      return `Paid · ${state.ticketCount} ${state.ticketCount === 1 ? "ticket" : "tickets"}`;
    default:
      return null;
  }
}
